import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_swimtrack-4/artifacts/manjzwlv_rolling%20river%20transparent.png";

export default function PasscodeScreen() {
  const { login } = useAuth();
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0);
  const inputRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockCountdown <= 0) {
      clearInterval(countdownRef.current);
      setDisabled(false);
      setError("");
      return;
    }

    countdownRef.current = setInterval(() => {
      setLockCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setDisabled(false);
          setError("");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownRef.current);
  }, [lockCountdown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (disabled || loading || !passcode.trim()) return;

    setLoading(true);
    setError("");

    try {
      const result = await login(passcode);

      if (result.success) return; // AuthContext handles the rest

      setPasscode("");

      if (result.locked) {
        setDisabled(true);
        setLockCountdown(result.remaining_seconds || 60);
        setError(`Too many failed attempts. Locked for ${result.remaining_seconds || 60} seconds.`);
      } else {
        // 2-second delay before allowing retry
        setDisabled(true);
        setError(result.message || "Invalid passcode");
        setTimeout(() => {
          setDisabled(false);
          inputRef.current?.focus();
        }, 2000);
      }
    } catch {
      setError("Connection error. Please try again.");
      setDisabled(true);
      setTimeout(() => setDisabled(false), 2000);
    } finally {
      setLoading(false);
    }
  }, [passcode, disabled, loading, login]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100"
      data-testid="passcode-screen"
    >
      <div className="w-full max-w-sm mx-4">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={LOGO_URL}
            alt="Rolling River Day Camp"
            className="h-24 w-auto mb-4"
            data-testid="passcode-logo"
          />
          <div className="text-center">
            <h1 className="text-lg font-semibold text-slate-700 tracking-tight">
              Rolling River Aquatics
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Staff Access Only</p>
          </div>
        </div>

        {/* Passcode form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-4 w-4 text-slate-400" />
              <label htmlFor="passcode" className="text-sm font-medium text-slate-600">
                Enter Passcode
              </label>
            </div>

            <Input
              ref={inputRef}
              id="passcode"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Enter staff passcode"
              disabled={disabled}
              autoComplete="off"
              className="text-center text-lg tracking-widest h-12 text-black bg-white"
              data-testid="passcode-input"
            />

            {error && (
              <div
                className="mt-3 text-sm text-red-600 text-center bg-red-50 rounded-lg py-2 px-3"
                data-testid="passcode-error"
              >
                {error}
                {lockCountdown > 0 && (
                  <div className="mt-1 font-mono text-red-700 font-semibold" data-testid="lockout-countdown">
                    {Math.floor(lockCountdown / 60)}:{String(lockCountdown % 60).padStart(2, "0")}
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={disabled || loading || !passcode.trim()}
              className="w-full mt-4 h-11 bg-[#1e3a5f] hover:bg-[#2d5a87] text-white font-medium"
              data-testid="passcode-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Enter"
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <p className="text-xs text-slate-400 text-center mt-6">
          Rolling River Day Camp &middot; Summer 2026
        </p>
      </div>
    </div>
  );
}
